import { ClockIcon } from "@heroicons/react/20/solid";
import type { ReactNode } from "react";
import { OtsFormDialog } from "../components/OtsFormDialog";
import type { OtsBrand, OtsColorSchemePreference } from "../theme/OtsTheme";
import type { ShiftFormNames, ShiftFormValues } from "./shift-form";
import { ShiftFormFields, type ShiftFormFieldsProps } from "./ShiftFormFields";
import { useShiftFormState } from "./useShiftFormState";

export type ShiftFormDialogProps = Omit<ShiftFormFieldsProps, "state"> & {
  open: boolean;
  /** An edit changes the stored shift; a new one is created. */
  mode: "new" | "edit";
  /** What the form starts with each time it opens, e.g. prefilled from the selected tickets. */
  initialValues: ShiftFormValues;
  /** Where the prefill came from, shown under the title. */
  description?: ReactNode;
  onSubmit: (values: ShiftFormValues, names: ShiftFormNames) => void;
  onClose: () => void;
  submitting?: boolean;
  /** A failed save; the dialog stays open to retry. */
  error?: ReactNode;
  /** Holds the form while the stored shift loads. */
  loading?: boolean;
  /** Starts the form over from `initialValues` when it changes, besides on opening; e.g. once the stored shift loads. */
  resetKey?: unknown;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

/** The shift form in a dialog, for creating or editing a shift without leaving the page. */
export const ShiftFormDialog = ({
  open,
  mode,
  initialValues,
  description,
  onSubmit,
  onClose,
  submitting = false,
  error,
  loading = false,
  resetKey,
  brand,
  colorScheme,
  ...fields
}: ShiftFormDialogProps) => {
  // Each opening starts from the prefill; nothing typed last time carries over.
  const state = useShiftFormState(initialValues, `${open}:${String(resetKey ?? "")}`);
  const title = mode === "edit" ? "Edit Shift" : "New Shift";
  return (
    <OtsFormDialog
      open={open}
      onClose={onClose}
      onSubmit={() => {
        if (!submitting && !loading && state.validate()) onSubmit(state.values, state.names);
      }}
      title={title}
      description={description}
      icon={<ClockIcon/>}
      formLabel={title}
      error={error}
      submitLabel={mode === "edit" ? "Save shift" : "Create shift"}
      submittingLabel="Saving shift…"
      progressLabel="Saving shift"
      submitting={submitting}
      submitDisabled={loading}
      size="2xl"
      brand={brand}
      colorScheme={colorScheme}>
      <ShiftFormFields {...fields} state={state}/>
    </OtsFormDialog>
  );
};
